import type { MaterialClass } from '@felix/domain';
import {
  ActionIcon,
  Alert,
  Button,
  Pagination,
  RingProgress,
  Select,
  Skeleton,
  Tabs,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import {
  IconAlertTriangleFilled,
  IconArrowsExchange,
  IconBell,
  IconBox,
  IconBuildingWarehouse,
  IconCalendar,
  IconChevronDown,
  IconChevronRight,
  IconCircleCheck,
  IconClipboardList,
  IconExclamationCircleFilled,
  IconEye,
  IconFileText,
  IconFilter,
  IconFolderFilled,
  IconPackageExport,
  IconPackageImport,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconShoppingCart,
  IconToolsKitchen2,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime, formatDay } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatTile } from '../../StatTile.js';
import { followLink, useShell } from '../../shell-context.js';
import { TOKENS } from '../../tokens.js';
import { errorCode } from '../personnel/source.js';
import type { PersonnelVessel } from '../personnel/types.js';
import { StockStatusBadge } from './badges.js';
import {
  filterStockItems,
  formatQuantity,
  formatSignedQuantity,
  formatUnit,
  inventoryTree,
  movementsOf,
  percent,
  sortStockItems,
  stockAlerts,
  stockDistribution,
  stockLocations,
  stockSummary,
} from './inventory.js';
import { TableSkeleton } from './MaterialMasterScreen.js';
import classes from './Materials.module.css';
import { DEFAULT_STOCK_SORT, SortTh, type StockSortState } from './SortHeader.js';
import stock from './Stock.module.css';
import { ItemHistoryDialog, MovementDialog } from './StockDialogs.js';
import type { MaterialsSource } from './source.js';
import type { StockItem, StockMovement, VesselStock } from './types.js';

/** Envanter listesi sayfa boyutları; tasarımda 8 satır (`gemi-envanter-kumanya.jpeg`; G5-30). */
const PAGE_SIZES = [8, 10, 20] as const;
/** Son hareketler kartında 4 satır (G5-51). */
const RECENT_ROWS = 4;
type Tab = 'all' | 'in' | 'out' | 'history';

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | {
      kind: 'ready';
      vessels: PersonnelVessel[];
      stock: VesselStock | null;
      movements: StockMovement[];
    };

export interface StockInOutScreenProps {
  source: MaterialsSource;
  /**
   * Gösterilen havuz (ADR 0122 §5): Inventory ▸ Spare Parts kumanya dışı malzemedir
   * (`stores`); kumanya Provision ekranındadır.
   */
  materialClass?: MaterialClass;
}

/**
 * Stok giriş ve çıkışı (`gemi-envanter-kumanya.jpeg` "Inventory & Provision"; ADR 0026 §1–2):
 * başlık kartı, özet kutuları, envanter ağacı, liste (All Items / Stock In / Stock Out / History),
 * stok seviye dağılımı, stok özeti, son hareketler, uyarılar ve hızlı işlemler. Gemide kaptan ve
 * başmühendis giriş ve çıkış yapar; ofiste salt okunur. Kumanya (Provision) Faz 2, satın alma
 * entegrasyonu M12b'de (M12 planı "Tasarımdan bilinçli farklar"). Başlık kartı sayfa başlığıdır;
 * h1 erişilebilirlik için gizli kalır (M6f G5-29).
 */
export function StockInOutScreen({ source, materialClass = 'stores' }: StockInOutScreenProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const shell = useShell();
  const k = (key: string, options: Record<string, unknown> = {}) => t(`stock.${key}`, options);
  const [vesselId, setVesselId] = useState<string | null>(null);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [tab, setTab] = useState<Tab>('all');
  const [tree, setTree] = useState<{ mainCategory: string | null; categoryId: string | null }>({
    mainCategory: null,
    categoryId: null,
  });
  const [treeSearch, setTreeSearch] = useState('');
  // Ağacın ilk seviyesi açık başlar (G5-46); kapatılan gruplar burada
  const [closed, setClosed] = useState<Set<string>>(new Set());
  const [draft, setDraft] = useState({ mainCategory: '', code: '', location: '', q: '' });
  const [filter, setFilter] = useState(draft);
  const [headSearch, setHeadSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);
  const [sort, setSort] = useState<StockSortState>(DEFAULT_STOCK_SORT);
  const [summaryBy, setSummaryBy] = useState<'category' | 'location'>('category');
  const [movement, setMovement] = useState<{
    kind: 'in' | 'out' | 'adjust';
    itemId: string | null;
  } | null>(null);
  const [history, setHistory] = useState<StockItem | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState((current) => (current.kind === 'ready' ? current : { kind: 'loading' }));
    try {
      const vessels = await source.vessels();
      const vessel = vessels.find((v) => v.id === vesselId) ?? vessels[0];
      const [loaded, movements] = vessel
        ? await Promise.all([
            source.stock(vessel.id, { materialClass }),
            source.movements(vessel.id, { limit: 200, materialClass }),
          ])
        : [null, []];
      setState({ kind: 'ready', vessels, stock: loaded, movements });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source, vesselId, materialClass]);
  useEffect(() => {
    void load();
  }, [load]);

  const ready = state.kind === 'ready' ? state : null;
  const items = ready?.stock?.items ?? [];
  const movements = ready?.movements ?? [];
  const vessel =
    ready?.vessels.find((v) => v.id === ready.stock?.vesselId) ?? ready?.vessels[0] ?? null;
  const today = ready?.stock?.today ?? '';
  const distribution = useMemo(() => stockDistribution(items), [items]);
  const nodes = useMemo(() => inventoryTree(items), [items]);
  const locations = useMemo(() => stockLocations(items), [items]);
  const summary = useMemo(() => stockSummary(items, summaryBy), [items, summaryBy]);
  const alerts = useMemo(() => stockAlerts(items), [items]);
  const listed = useMemo(
    () =>
      sortStockItems(
        filterStockItems(filterStockItems(items, { q: headSearch }), {
          q: filter.q,
          code: filter.code,
          mainCategory: tree.mainCategory ?? (filter.mainCategory || null),
          categoryId: tree.categoryId,
          location: filter.location || null,
        }),
        sort.column,
        locale,
        sort.direction,
      ),
    [items, filter, headSearch, tree, sort, locale],
  );
  const sortTh = (
    column: StockSortState['column'],
    label: string,
    numeric = false,
    col?: string,
  ) => (
    <SortTh
      column={column}
      label={label}
      sort={sort}
      numeric={numeric}
      col={col}
      onSort={(next) => {
        setSort(next);
        setPage(1);
      }}
    />
  );
  const moves = useMemo(() => (tab === 'all' ? [] : movementsOf(movements, tab)), [movements, tab]);
  const rows = tab === 'all' ? listed.length : moves.length;
  const pages = Math.max(1, Math.ceil(rows / pageSize));
  const current = Math.min(page, pages);
  const slice = <T,>(list: readonly T[]) =>
    list.slice((current - 1) * pageSize, current * pageSize);
  const editor = source.stockEditor;
  const n = (value: number) => formatQuantity(value, locale);
  const qty = (value: number, unit: string) => `${n(value)} ${formatUnit(unit)}`;
  // Hazır olmadan sayı yazılmaz; yüklenirken ve hatada "—" (G5-33)
  const count = (value: number) => (ready ? n(value) : '—');
  const go = (path: string) => shell?.navigate(path);
  /** Hareketin gemi yerel tarihi ve saati (CLAUDE.md "Zaman"). */
  const movedAt = (move: StockMovement) => formatDateTime(move.createdAt, move.tzOffsetMin);
  const lastMove = (item: StockItem) => movements.find((m) => m.stockItemId === item.id) ?? null;

  const openMovement = (kind: 'in' | 'out' | 'adjust', itemId: string | null = null) => {
    setNotice(null);
    setMovement({ kind, itemId });
  };
  const chooseTree = (mainCategory: string | null, categoryId: string | null = null) => {
    setTree({ mainCategory, categoryId });
    setTab('all');
    setPage(1);
  };

  const fact = (label: string, value: ReactNode, icon?: ReactNode) => (
    <div>
      <dt>{label}</dt>
      <dd>
        {icon ? (
          <span className={stock.factIcon} aria-hidden>
            {icon}
          </span>
        ) : null}
        <span>{value}</span>
      </dd>
    </div>
  );

  const quick = (
    label: string,
    icon: ReactNode,
    tone: 'success' | 'info' | 'purple' | 'warning',
    action: () => void,
    path?: string,
  ) => {
    const body = (
      <>
        <span className={stock.quickIcon} aria-hidden>
          {icon}
        </span>
        <span className={stock.quickLabel}>{label}</span>
      </>
    );
    return path ? (
      <a
        href={path}
        className={stock.quick}
        data-tone={tone}
        data-tinted
        title={label}
        onClick={(event) => {
          if (shell) followLink(event, path, shell.navigate);
        }}
      >
        {body}
      </a>
    ) : (
      <UnstyledButton className={stock.quick} data-tone={tone} title={label} onClick={action}>
        {body}
      </UnstyledButton>
    );
  };

  const treeMatches = (text: string) =>
    !treeSearch.trim() ||
    text.toLocaleLowerCase(locale).includes(treeSearch.trim().toLocaleLowerCase(locale));

  const pager = (
    <div className={stock.pager}>
      <span>
        {k('showing', {
          from: rows === 0 ? 0 : (current - 1) * pageSize + 1,
          to: Math.min(current * pageSize, rows),
          total: rows,
        })}
      </span>
      <div className={classes.pagerEnd}>
        {pages > 1 ? (
          <Pagination
            total={pages}
            value={current}
            onChange={setPage}
            aria-label={k('pagination')}
            getControlProps={(control) => ({ 'aria-label': t(`materials.page.${control}`) })}
            getItemProps={(number) => ({
              'aria-label': t('materials.page.number', { page: number }),
            })}
          />
        ) : null}
        {rows > PAGE_SIZES[0] ? (
          <Select
            size="xs"
            className={classes.pageSize}
            aria-label={k('pageSize')}
            data={PAGE_SIZES.map((size) => ({
              value: String(size),
              label: k('perPage', { size }),
            }))}
            value={String(pageSize)}
            allowDeselect={false}
            onChange={(value) => {
              setPageSize(Number(value ?? PAGE_SIZES[0]));
              setPage(1);
            }}
          />
        ) : null}
      </div>
    </div>
  );

  return (
    <div className={`${classes.screen} ${stock.inOut}`}>
      <PageHeader
        title={t('menu.inventorySpareParts')}
        titleHidden
        actions={
          source.scope === 'web' && ready && ready.vessels.length > 1 ? (
            <Select
              size="xs"
              aria-label={t('shell.selectVessel')}
              data={ready.vessels.map((v) => ({ value: v.id, label: v.name }))}
              value={vessel?.id ?? null}
              allowDeselect={false}
              onChange={(value) => {
                setVesselId(value);
                setTree({ mainCategory: null, categoryId: null });
                setPage(1);
              }}
            />
          ) : null
        }
      />

      {state.kind === 'error' ? (
        <Panel>
          <EmptyState
            tone="danger"
            title={k('error')}
            action={
              <Button
                size="xs"
                variant="light"
                leftSection={<IconRefresh size={14} />}
                onClick={() => void load()}
              >
                {k('retry')}
              </Button>
            }
          />
        </Panel>
      ) : null}
      {notice ? (
        <Alert
          color="green"
          icon={<IconCircleCheck size={18} />}
          withCloseButton
          onClose={() => setNotice(null)}
        >
          {notice}
        </Alert>
      ) : null}

      <div className={stock.top}>
        <section className={stock.head} aria-label={k('inOut.head')}>
          <div className={stock.headTitle}>
            <span className={stock.headIcon} aria-hidden>
              <IconBox size={30} stroke={1.5} />
            </span>
            <div className={stock.headText}>
              <strong>{k('inOut.headTitle')}</strong>
              <nav className={stock.headLinks} aria-label={k('inOut.sections')}>
                <a
                  href="/inventory/spare-parts"
                  aria-current="page"
                  onClick={(event) => event.preventDefault()}
                >
                  {t('menu.inventorySpareParts')}
                </a>
                <span aria-hidden>|</span>
                <a
                  href="/inventory/provision"
                  onClick={(event) =>
                    shell && followLink(event, '/inventory/provision', shell.navigate)
                  }
                >
                  {t('menu.inventoryProvision')}
                </a>
                <span aria-hidden>|</span>
                <a
                  href="/purchasing/requisitions"
                  onClick={(event) =>
                    shell && followLink(event, '/purchasing/requisitions', shell.navigate)
                  }
                >
                  {k('inOut.purchasingIntegration')}
                </a>
              </nav>
            </div>
          </div>
          <dl className={stock.headFacts}>
            {fact(k('vessel'), vessel?.name ?? '—')}
            {fact(k('inOut.imo'), vessel?.imo ?? '—')}
            {fact(k('inOut.locations'), count(locations.length))}
            {fact(
              k('inOut.date'),
              today ? formatDay(today) : '—',
              <IconCalendar size={16} stroke={1.6} />,
            )}
          </dl>
        </section>
        <div className={stock.headActions}>
          <TextInput
            className={stock.headSearch}
            leftSection={<IconSearch size={16} aria-hidden />}
            placeholder={k('inOut.searchPlaceholder')}
            aria-label={k('inOut.searchPlaceholder')}
            value={headSearch}
            onChange={(event) => {
              setHeadSearch(event.currentTarget.value);
              setTab('all');
              setPage(1);
            }}
          />
          {editor ? (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => openMovement('in')}
              disabled={ready !== null && items.length === 0}
            >
              {k('inOut.newRecord')}
            </Button>
          ) : null}
        </div>
      </div>

      <div className={stock.layout}>
        <section className={stock.tiles} aria-label={k('inOut.summary')}>
          {ready ? (
            <>
              <StatTile
                variant="plain"
                icon={<IconBuildingWarehouse size={22} />}
                label={k('inOut.total')}
                value={n(distribution.total)}
              />
              <StatTile
                variant="plain"
                icon={<IconAlertTriangleFilled size={20} />}
                label={k('inOut.low')}
                value={n(distribution.low)}
                tone="warning"
              />
              <StatTile
                variant="plain"
                icon={<IconExclamationCircleFilled size={22} />}
                label={k('inOut.critical')}
                value={n(distribution.critical)}
                tone="danger"
              />
              <StatTile
                variant="plain"
                icon={<IconPackageImport size={22} />}
                label={k('inOut.todayIn')}
                value={n(ready.stock?.todayIn ?? 0)}
                tone="success"
              />
              <StatTile
                variant="plain"
                icon={<IconPackageExport size={22} />}
                label={k('inOut.todayOut')}
                value={n(ready.stock?.todayOut ?? 0)}
              />
            </>
          ) : (
            [0, 1, 2, 3, 4].map((i) => <Skeleton key={i} height={72} radius="md" />)
          )}
        </section>

        <Panel title={k('inOut.tree')} className={stock.tree}>
          <TextInput
            size="xs"
            leftSection={<IconSearch size={14} aria-hidden />}
            rightSection={<IconFilter size={14} aria-hidden />}
            placeholder={k('inOut.treeSearch')}
            aria-label={k('inOut.treeSearch')}
            value={treeSearch}
            onChange={(event) => setTreeSearch(event.currentTarget.value)}
          />
          {ready ? (
            <ul className={stock.treeList}>
              <li>
                <div className={stock.treeRow}>
                  <span className={stock.treeToggle} aria-hidden>
                    <IconChevronDown size={14} />
                  </span>
                  <UnstyledButton
                    className={stock.treeNode}
                    aria-pressed={tree.mainCategory === null}
                    onClick={() => chooseTree(null)}
                  >
                    <IconFolderFilled size={16} aria-hidden className={stock.folder} />
                    <span>{k('inOut.allInventory')}</span>
                    <span className={stock.count}>{items.length}</span>
                  </UnstyledButton>
                </div>
                <ul>
                  {nodes
                    .filter(
                      (node) =>
                        treeMatches(node.mainCategory) ||
                        node.children.some((c) => treeMatches(c.subCategory)),
                    )
                    .map((node) => {
                      const expanded = !closed.has(node.mainCategory) || treeSearch.trim() !== '';
                      return (
                        <li key={node.mainCategory}>
                          <div className={stock.treeRow}>
                            <ActionIcon
                              size="sm"
                              variant="subtle"
                              color="gray"
                              className={stock.treeToggle}
                              aria-label={k(expanded ? 'inOut.collapse' : 'inOut.expand', {
                                name: node.mainCategory,
                              })}
                              aria-expanded={expanded}
                              onClick={() =>
                                setClosed((set) => {
                                  const next = new Set(set);
                                  if (next.has(node.mainCategory)) next.delete(node.mainCategory);
                                  else next.add(node.mainCategory);
                                  return next;
                                })
                              }
                            >
                              {expanded ? (
                                <IconChevronDown size={14} />
                              ) : (
                                <IconChevronRight size={14} />
                              )}
                            </ActionIcon>
                            <UnstyledButton
                              className={stock.treeNode}
                              aria-pressed={
                                tree.mainCategory === node.mainCategory && tree.categoryId === null
                              }
                              onClick={() => chooseTree(node.mainCategory)}
                            >
                              <IconFolderFilled size={16} aria-hidden className={stock.folder} />
                              <span>{node.mainCategory}</span>
                              <span className={stock.count}>{node.count}</span>
                            </UnstyledButton>
                          </div>
                          {expanded ? (
                            <ul>
                              {node.children.map((child) => (
                                <li key={child.categoryId}>
                                  <UnstyledButton
                                    className={stock.treeLeaf}
                                    aria-pressed={tree.categoryId === child.categoryId}
                                    onClick={() => chooseTree(node.mainCategory, child.categoryId)}
                                  >
                                    <IconFileText size={15} aria-hidden />
                                    <span>{child.subCategory}</span>
                                    <span className={stock.count}>{child.count}</span>
                                  </UnstyledButton>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </li>
                      );
                    })}
                </ul>
              </li>
            </ul>
          ) : (
            <div className={stock.treeSkeleton} aria-hidden>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} height={12} width={i === 0 ? '70%' : '55%'} radius="sm" />
              ))}
            </div>
          )}
        </Panel>

        <Panel title={k('inOut.list')} flush className={stock.list}>
          <Tabs
            value={tab}
            onChange={(value) => {
              setTab((value ?? 'all') as Tab);
              setPage(1);
            }}
            className={stock.tabs}
          >
            <Tabs.List>
              {(['all', 'in', 'out', 'history'] as const).map((value) => (
                <Tabs.Tab key={value} value={value}>
                  {k(`inOut.tabs.${value}`)}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>
          {tab === 'all' ? (
            <form
              className={stock.listFilters}
              aria-label={k('filters')}
              onSubmit={(event) => {
                event.preventDefault();
                setFilter(draft);
                setPage(1);
              }}
            >
              <Select
                label={k('category')}
                data={[
                  { value: '', label: k('all') },
                  ...nodes.map((node) => ({ value: node.mainCategory, label: node.mainCategory })),
                ]}
                value={draft.mainCategory}
                allowDeselect={false}
                onChange={(value) => {
                  setDraft((d) => ({ ...d, mainCategory: value ?? '' }));
                  setTree({ mainCategory: null, categoryId: null });
                }}
              />
              <TextInput
                label={k('inOut.code')}
                placeholder={k('inOut.anyCode')}
                className={stock.codeInput}
                value={draft.code}
                onChange={(event) => {
                  const code = event.currentTarget.value;
                  setDraft((d) => ({ ...d, code }));
                }}
              />
              <Select
                label={k('inOut.location')}
                data={[
                  { value: '', label: k('all') },
                  ...locations.map((value) => ({ value, label: value })),
                ]}
                value={draft.location}
                allowDeselect={false}
                onChange={(value) => setDraft((d) => ({ ...d, location: value ?? '' }))}
              />
              <TextInput
                aria-label={k('inOut.item')}
                placeholder={k('inOut.searchItem')}
                leftSection={<IconSearch size={14} aria-hidden />}
                value={draft.q}
                onChange={(event) => {
                  const q = event.currentTarget.value;
                  setDraft((d) => ({ ...d, q }));
                }}
              />
              <Button type="submit">{k('search')}</Button>
            </form>
          ) : null}

          <div className={stock.listBody}>
            {state.kind !== 'ready' ? (
              <div className={stock.tableBox}>
                <TableSkeleton columns={8} rows={PAGE_SIZES[0]} />
              </div>
            ) : items.length === 0 ? (
              <EmptyState
                title={k('emptyTitle')}
                description={editor ? k('emptyShip') : k('emptyWeb')}
              />
            ) : rows === 0 ? (
              <EmptyState
                icon={<IconSearch size={22} stroke={1.8} />}
                title={tab === 'all' ? k('noMatch') : k('inOut.noMovements')}
              />
            ) : (
              <section
                className={`${classes.tableWrap} ${stock.tableBox}`}
                aria-label={k(`inOut.tabs.${tab}`)}
                // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
                tabIndex={0}
              >
                {tab === 'all' ? (
                  <table className={`${classes.table} ${stock.inventoryTable}`}>
                    <thead>
                      <tr>
                        <th scope="col">
                          <span className={stock.visuallyHidden}>{k('columns.number')}</span>
                        </th>
                        {sortTh('name', k('columns.itemName'))}
                        <th scope="col" data-col="code">
                          {k('columns.issa')}
                        </th>
                        <th scope="col" data-col="code">
                          {k('columns.impa')}
                        </th>
                        {sortTh('category', k('columns.category'), false, 'category')}
                        <th scope="col">{k('columns.unit')}</th>
                        <th scope="col" data-col="location">
                          {k('columns.location')}
                        </th>
                        {sortTh('onHand', k('columns.onHand'), true)}
                        <th scope="col" data-numeric>
                          {k('columns.minLevel')}
                        </th>
                        {sortTh('status', k('columns.status'))}
                        <th scope="col" data-col="actions">
                          {k('columns.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {slice(listed).map((item, index) => (
                        <tr key={item.id}>
                          <td className={classes.secondary}>
                            {(current - 1) * pageSize + index + 1}
                          </td>
                          <td>
                            {/* Ad geçmiş penceresini açar: bağlantı görünümlü düğme (G5-36) */}
                            <UnstyledButton
                              className={stock.linkButton}
                              aria-haspopup="dialog"
                              title={k('historyOf', { name: item.name })}
                              onClick={() => setHistory(item)}
                            >
                              {item.name}
                            </UnstyledButton>
                          </td>
                          <td data-col="code">{item.issaCode ?? '—'}</td>
                          <td data-col="code">{item.impaCode ?? '—'}</td>
                          <td data-col="category">
                            <UnstyledButton
                              className={stock.linkButton}
                              title={k('inOut.showCategory', { name: item.subCategory })}
                              onClick={() => chooseTree(item.mainCategory, item.categoryId)}
                            >
                              {item.subCategory}
                            </UnstyledButton>
                          </td>
                          <td>{formatUnit(item.unit)}</td>
                          <td data-col="location">{item.location ?? '—'}</td>
                          <td data-numeric>{n(item.onHand)}</td>
                          <td data-numeric>{n(item.minQty)}</td>
                          <td>
                            <StockStatusBadge status={item.status} />
                          </td>
                          <td className={stock.actions} data-col="actions">
                            <ActionIcon
                              variant="default"
                              size="sm"
                              aria-label={k('historyOf', { name: item.name })}
                              onClick={() => setHistory(item)}
                            >
                              <IconEye size={15} />
                            </ActionIcon>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <table className={`${classes.table} ${stock.movementTable}`}>
                    <thead>
                      <tr>
                        <th scope="col">{k('columns.date')}</th>
                        <th scope="col">{k('columns.type')}</th>
                        <th scope="col">{k('columns.itemName')}</th>
                        <th scope="col" data-numeric>
                          {k('columns.qty')}
                        </th>
                        <th scope="col">{k('columns.location')}</th>
                        <th scope="col">{k('columns.user')}</th>
                        <th scope="col">{k('columns.reason')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {slice(moves).map((move) => (
                        <tr key={move.id}>
                          <td>{movedAt(move)}</td>
                          <td>{t(`stock.kinds.${move.kind}`, { defaultValue: move.kind })}</td>
                          <td className={stock.link}>{move.materialName}</td>
                          <td data-numeric>
                            {`${formatSignedQuantity(move.quantity, locale)} ${formatUnit(move.unit)}`}
                          </td>
                          <td>{move.location ?? '—'}</td>
                          <td>{move.createdByName ?? '—'}</td>
                          <td className={stock.reason} title={move.reason ?? undefined}>
                            {move.reason ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            )}
          </div>
          {ready && rows > 0 ? pager : null}
        </Panel>

        <div className={stock.side}>
          <Panel title={k('inOut.distribution')} className={stock.distribution}>
            <div className={stock.ring}>
              {ready ? (
                <RingProgress
                  size={100}
                  thickness={12}
                  roundCaps={false}
                  sections={
                    distribution.total === 0
                      ? [{ value: 100, color: TOKENS['page-bg'] }]
                      : [
                          {
                            value: (distribution.normal / distribution.total) * 100,
                            color: TOKENS['success-fg'],
                          },
                          {
                            value: (distribution.low / distribution.total) * 100,
                            color: TOKENS['warning-500'],
                          },
                          {
                            value: (distribution.critical / distribution.total) * 100,
                            color: TOKENS['danger-fg'],
                          },
                        ]
                  }
                  label={<strong className={stock.ringValue}>{n(distribution.total)}</strong>}
                  aria-hidden
                />
              ) : (
                <Skeleton height={100} width={100} circle />
              )}
              <ul className={stock.legend}>
                {(['normal', 'low', 'critical'] as const).map((status) => (
                  <li key={status}>
                    <span className={stock.legendDot} data-status={status} aria-hidden />
                    <span className={stock.legendLabel}>{k(`status.inventory.${status}`)}</span>
                    <span className={classes.number}>
                      {ready
                        ? `${n(distribution[status])} (${percent(distribution[status], distribution.total)}%)`
                        : '—'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </Panel>

          <Panel
            title={k('inOut.stockSummary')}
            className={stock.summary}
            extra={
              <ActionIcon
                variant="subtle"
                size="sm"
                aria-label={k('inOut.refresh')}
                onClick={() => void load()}
              >
                <IconRefresh size={16} />
              </ActionIcon>
            }
          >
            <Tabs
              value={summaryBy}
              onChange={(value) => setSummaryBy((value ?? 'category') as 'category' | 'location')}
              className={stock.summaryTabs}
            >
              <Tabs.List>
                <Tabs.Tab value="category">{k('inOut.byCategory')}</Tabs.Tab>
                <Tabs.Tab value="location">{k('inOut.byLocation')}</Tabs.Tab>
              </Tabs.List>
            </Tabs>
            {!ready ? (
              <Skeleton height={160} radius="sm" />
            ) : items.length === 0 ? (
              <p className={classes.muted}>{k('inOut.noSummary')}</p>
            ) : (
              <div className={stock.miniBox}>
                <table className={classes.miniTable}>
                  <thead>
                    <tr>
                      <th scope="col">
                        {summaryBy === 'category' ? k('columns.category') : k('columns.location')}
                      </th>
                      <th scope="col" data-numeric>
                        {k('inOut.items')}
                      </th>
                      <th scope="col" data-numeric>
                        {k('status.inventory.low')}
                      </th>
                      <th scope="col" data-numeric>
                        {k('status.inventory.critical')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.rows.map((row) => (
                      <tr key={row.key || '-'}>
                        <td>
                          {row.key && summaryBy === 'category' ? (
                            <UnstyledButton
                              className={stock.linkButton}
                              onClick={() => chooseTree(row.key)}
                            >
                              {row.key}
                            </UnstyledButton>
                          ) : (
                            <span className={row.key ? stock.link : undefined}>
                              {row.key || k('inOut.noLocation')}
                            </span>
                          )}
                        </td>
                        <td data-numeric>{n(row.items)}</td>
                        <td data-numeric>{n(row.low)}</td>
                        <td data-numeric>{n(row.critical)}</td>
                      </tr>
                    ))}
                    <tr className={stock.totalRow}>
                      <td>{k('inOut.totalRow')}</td>
                      <td data-numeric>{n(summary.total.items)}</td>
                      <td data-numeric>{n(summary.total.low)}</td>
                      <td data-numeric>{n(summary.total.critical)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>
      </div>

      <div className={stock.bottom}>
        <Panel
          title={k('inOut.recent')}
          icon={<IconArrowsExchange size={18} />}
          className={stock.bottomCard}
        >
          {!ready ? (
            <Skeleton height={120} radius="sm" />
          ) : movements.length === 0 ? (
            <p className={classes.muted}>{k('inOut.noMovements')}</p>
          ) : (
            <section
              className={`${stock.scroll} ${stock.miniBox}`}
              aria-label={k('inOut.recentTable')}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={`${classes.miniTable} ${stock.recentTable}`}>
                <thead>
                  <tr>
                    <th scope="col">{k('columns.date')}</th>
                    <th scope="col">{k('columns.type')}</th>
                    <th scope="col">{k('columns.item')}</th>
                    <th scope="col" data-numeric>
                      {k('columns.qty')}
                    </th>
                    <th scope="col">{k('columns.location')}</th>
                    <th scope="col">{k('columns.user')}</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.slice(0, RECENT_ROWS).map((move) => (
                    <tr key={move.id}>
                      <td className={stock.link}>{movedAt(move)}</td>
                      <td>{t(`stock.kinds.${move.kind}`, { defaultValue: move.kind })}</td>
                      {/* Uzun ad tek satır, tam metin title'da (yedek parça adları uzun) */}
                      <td className={`${stock.link} ${stock.recentItem}`} title={move.materialName}>
                        {move.materialName}
                      </td>
                      <td data-numeric>{qty(Math.abs(move.quantity), move.unit)}</td>
                      <td className={stock.link}>{move.location ?? '—'}</td>
                      <td>{move.createdByName ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </Panel>

        <Panel
          title={k('inOut.alerts')}
          icon={<IconBell size={18} />}
          className={stock.bottomCard}
          extra={
            ready && alerts.length > 0 ? (
              <Button
                size="compact-xs"
                variant="outline"
                onClick={() => {
                  setTab('all');
                  setTree({ mainCategory: null, categoryId: null });
                  setFilter({ mainCategory: '', code: '', location: '', q: '' });
                  setDraft({ mainCategory: '', code: '', location: '', q: '' });
                  setSort({ column: 'status', direction: 'asc' });
                  setPage(1);
                }}
              >
                {k('inOut.viewAll')}
              </Button>
            ) : null
          }
        >
          {!ready ? (
            <Skeleton height={120} radius="sm" />
          ) : alerts.length === 0 ? (
            <p className={classes.muted}>{k('inOut.noAlerts')}</p>
          ) : (
            <ul className={stock.alertList}>
              {alerts.slice(0, 4).map((item) => {
                // Düşükte "en az seviyede" ile "altında" ayrı; metin tek satır (G5-42)
                const kind =
                  item.status === 'critical'
                    ? 'critical'
                    : item.onHand < item.minQty
                      ? 'lowBelow'
                      : 'lowAt';
                const text = k(`inOut.alert.${kind}`, {
                  name: item.name,
                  quantity: qty(item.onHand, item.unit),
                });
                const detail = k('inOut.alertDetail', {
                  onHand: qty(item.onHand, item.unit),
                  min: qty(item.minQty, item.unit),
                });
                const move = lastMove(item);
                return (
                  <li key={item.id}>
                    <span className={stock.alertIcon} data-tone={item.status} aria-hidden>
                      {item.status === 'critical' ? (
                        <IconExclamationCircleFilled size={20} />
                      ) : (
                        <IconAlertTriangleFilled size={18} />
                      )}
                    </span>
                    <UnstyledButton
                      className={stock.alertText}
                      title={`${text} · ${detail}`}
                      onClick={() => setHistory(item)}
                    >
                      {text}
                    </UnstyledButton>
                    <span className={stock.alertTime}>
                      {move ? movedAt(move) : formatDay(item.lastMovementDate)}
                    </span>
                    <IconChevronRight size={14} aria-hidden className={stock.alertChevron} />
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        <Panel
          title={k('inOut.quickActions')}
          icon={<IconClipboardList size={18} />}
          className={stock.bottomCard}
        >
          <div className={stock.quickGrid}>
            {editor ? (
              <>
                {quick(k('inOut.addIn'), <IconPackageImport size={17} />, 'success', () =>
                  openMovement('in'),
                )}
                {quick(k('inOut.addOut'), <IconPackageExport size={17} />, 'info', () =>
                  openMovement('out'),
                )}
              </>
            ) : null}
            {quick(
              k('inOut.provisionRequest'),
              <IconToolsKitchen2 size={17} />,
              'purple',
              () => go('/inventory/provision'),
              '/inventory/provision',
            )}
            {quick(
              k('inOut.purchasingRequests'),
              <IconShoppingCart size={17} />,
              'warning',
              () => go('/purchasing/requisitions'),
              '/purchasing/requisitions',
            )}
          </div>
          {editor ? null : <p className={classes.muted}>{k('inOut.readOnly')}</p>}
        </Panel>
      </div>

      {editor && ready?.stock ? (
        <MovementDialog
          opened={movement !== null}
          kind={movement?.kind ?? 'in'}
          itemId={movement?.itemId ?? null}
          items={items}
          today={today}
          editor={editor}
          onClose={() => setMovement(null)}
          onSaved={({ movement: saved }) => {
            setMovement(null);
            setNotice(
              k(`inOut.saved.${saved.kind}`, {
                name: saved.materialName,
                quantity: n(Math.abs(saved.quantity)),
                unit: formatUnit(saved.unit),
              }),
            );
            void load();
          }}
        />
      ) : null}
      <ItemHistoryDialog
        item={history}
        source={source}
        onClose={() => setHistory(null)}
        onMovement={
          editor && history
            ? (kind) => {
                setHistory(null);
                openMovement(kind, history.id);
              }
            : undefined
        }
      />
    </div>
  );
}
